import { type JSX, type KeyboardEvent, type ReactNode, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTextSelection } from "./useTextSelection";

/**
 * In-place ask actions. R★.1a deliberately offers only TWO peer actions in the
 * popover — 解释这段 / 我来问 — so "解释 vs 深入 分不清" never happens (#06 is
 * dissolved, not deferred). deepen/related return in R★.2 as post-answer
 * follow-up chips, where their position makes the difference self-evident.
 */
export type AskMode = "explain" | "deepen" | "related" | "ask";

interface Props {
  /** range: the live selection range — the reader pins it as the turn's anchor (#04/#08). */
  onAsk: (passage: string, mode: AskMode, range?: Range) => void;
  children: ReactNode;
  /**
   * #12 (stage 1c+): keyboard access — the popover takes focus on open, arrows
   * move between actions, Esc dismisses. Off below 1c (staged rollout).
   */
  a11y?: boolean;
  /**
   * #05 tier-A (small screens below stage 1c): selecting shows an HONEST
   * degrade note (问答请用电脑端) instead of broken actions.
   */
  degraded?: boolean;
}

const ACTIONS: { mode: AskMode; label: string; caption: string; primary?: boolean }[] = [
  { mode: "explain", label: "解释这段", caption: "用大白话讲", primary: true },
  { mode: "ask", label: "我来问", caption: "自己提问" }
];

/** Initial clamp-box estimate; replaced by the measured size after render (#07). */
const POP_W = 220;
const POP_H = 48;

/**
 * Wraps the reading pane (FEATURE_MANUAL_ASK). On a text selection inside it, a
 * popover offers 解释这段/我来问; choosing one pins the passage as a persistent
 * anchor highlight (#04) and fires onAsk(passage, mode).
 *
 * Positioning (#07): the popover is position:fixed and uses the selection
 * rect's VIEWPORT coordinates directly — the reading pane scrolls inside
 * .guides-main (window never scrolls), so the old absolute+scrollY math drifted
 * by exactly the container's scroll offset. Fixed coords can't drift.
 */
export function SelectionLayer({ onAsk, children, a11y, degraded }: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const selection = useTextSelection({ minChars: 2, maxChars: 2000, debounceMs: 120, containerRef });
  // Clamp against the popover's REAL size — button copy (or future locales) can
  // outgrow the estimate and would otherwise overflow the viewport edge.
  const [popSize, setPopSize] = useState({ w: POP_W, h: POP_H });
  useLayoutEffect(() => {
    const el = popRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (Math.abs(r.width - popSize.w) > 1 || Math.abs(r.height - popSize.h) > 1) {
      setPopSize({ w: r.width, h: r.height });
    }
  }, [selection, popSize.w, popSize.h]);

  // #12: keyboard users can reach the actions — focus the first one on open.
  // (Programmatic focus does not collapse the text selection.)
  useEffect(() => {
    if (a11y !== true || !selection || degraded === true) return;
    popRef.current?.querySelector("button")?.focus();
  }, [selection, a11y, degraded]);

  function fire(mode: AskMode): void {
    if (!selection) return;
    const passage = selection.text;
    const sel = window.getSelection();
    // #04: hand the live range up — the reader pins it as a persistent anchor
    // highlight (the browser selection itself is cleared right after). Known
    // limit: past maxChars the QUESTION is truncated but the highlight still
    // covers the full selection — shrinking a multi-node range to a character
    // count isn't worth the complexity for a 2000-char edge.
    const range = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : undefined;
    sel?.removeAllRanges();
    onAsk(passage, mode, range);
  }

  // #12: Esc dismisses, ←/→ move between the two actions.
  function onPopKeyDown(e: KeyboardEvent<HTMLDivElement>): void {
    if (a11y !== true) return;
    if (e.key === "Escape") {
      window.getSelection()?.removeAllRanges();
      return;
    }
    if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      e.preventDefault();
      const btns = [...(popRef.current?.querySelectorAll("button") ?? [])];
      const i = btns.indexOf(document.activeElement as HTMLButtonElement);
      const next = e.key === "ArrowRight" ? (i + 1) % btns.length : (i - 1 + btns.length) % btns.length;
      btns[next]?.focus();
    }
  }

  const pop = selection
    ? (() => {
        // Prefer above the selection; flip below when clipped at the top (#07
        // vertical clamp — the old code could push the popover off-screen).
        const above = selection.rect.top - popSize.h;
        const top =
          above >= 8 ? above : Math.min(selection.rect.bottom + 8, window.innerHeight - popSize.h - 8);
        const left = Math.min(Math.max(8, selection.rect.left), window.innerWidth - popSize.w - 8);
        return { top, left };
      })()
    : null;

  return (
    <div ref={containerRef} className="guides-selectable">
      {children}
      {selection && pop && (
        <div
          ref={popRef}
          className="guides-sel-pop"
          style={{ top: pop.top, left: pop.left }}
          role="menu"
          aria-label="就选中的内容提问"
          onMouseDown={(e) => e.preventDefault()} // keep the selection while clicking
          onKeyDown={onPopKeyDown}
        >
          {degraded === true ? (
            <span className="guides-sel-degrade">问答请用桌面端打开</span>
          ) : (
            ACTIONS.map((a) => (
              <button
                key={a.mode}
                type="button"
                role="menuitem"
                className={`guides-sel-btn ${a.primary ? "primary" : ""}`}
                onClick={() => fire(a.mode)}
                onTouchEnd={(e) => {
                  // Touch: fire before the tap collapses the native selection
                  // (mousedown preventDefault has no effect on touch, #05).
                  e.preventDefault();
                  fire(a.mode);
                }}
              >
                <span className="guides-sel-lab">{a.label}</span>
                <span className="guides-sel-cap">{a.caption}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
